import { Fragment, useCallback, useMemo, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import type { RichTextProps } from './RichText.types'
import type { PostFacet } from '@/types/posts'

/**
 * Convert UTF-8 byte offset to JavaScript string character offset.
 * Bluesky facets use UTF-8 byte indices; JS strings are UTF-16.
 */
function byteToCharOffset(text: string, byteOffset: number): number {
    let bytes = 0
    for (let i = 0; i < text.length; i++) {
        if (bytes >= byteOffset) return i
        const char = text.codePointAt(i)!
        if (char <= 0x7f) bytes += 1
        else if (char <= 0x7ff) bytes += 2
        else if (char <= 0xffff) bytes += 3
        else {
            bytes += 4
            i++ // skip surrogate pair
        }
    }
    return text.length
}

interface FacetSegment {
    text: string
    facet?: PostFacet
}

/**
 * Split text into segments based on facet byte ranges.
 * Each segment is either plain text or faceted text.
 */
function segmentByFacets(text: string, facets: PostFacet[]): FacetSegment[] {
    // Sort facets by byte start position
    const sorted = [...facets].sort((a, b) => a.index.byteStart - b.index.byteStart)

    const segments: FacetSegment[] = []
    let charPos = 0

    for (const facet of sorted) {
        const start = byteToCharOffset(text, facet.index.byteStart)
        const end = byteToCharOffset(text, facet.index.byteEnd)

        // Skip invalid ranges and overlapping facets
        if (start >= end || start >= text.length || start < charPos) continue

        // Add plain text before this facet
        if (start > charPos) {
            segments.push({ text: text.slice(charPos, start) })
        }

        // Add the faceted segment
        segments.push({ text: text.slice(start, end), facet })
        charPos = end
    }

    // Add remaining text after last facet
    if (charPos < text.length) {
        segments.push({ text: text.slice(charPos) })
    }

    return segments
}

export const RichText = ({
    text,
    facets,
    disableMentions = false,
    className = '',
    trailing,
}: RichTextProps) => {
    const navigate = useNavigate()

    const handleMentionClick = useCallback(
        (e: React.MouseEvent, handle: string) => {
            e.preventDefault()
            e.stopPropagation()
            void navigate(`/profile/${handle}`)
        },
        [navigate]
    )

    const handleHashtagClick = useCallback(
        (e: React.MouseEvent, tag: string) => {
            e.preventDefault()
            e.stopPropagation()
            void navigate(`/hashtag/${encodeURIComponent(tag)}`)
        },
        [navigate]
    )
    if (!text) return null

    // --- Facet-based rendering ---
    if (facets?.length) {
        return (
            <FacetRenderer
                text={text}
                facets={facets}
                disableMentions={disableMentions}
                className={className}
                trailing={trailing}
                onMentionClick={handleMentionClick}
                onHashtagClick={handleHashtagClick}
            />
        )
    }

    // --- Fallback: regex-based rendering ---
    const renderLine = (line: string) => {
        const words = line.split(' ')
        return words.map((word, index) => {
            // Check if the word is truncated
            if (word.endsWith('...')) {
                return (
                    <Fragment key={index}>
                        {word}
                        {index !== words.length - 1 ? ' ' : ''}
                    </Fragment>
                )
            }

            // Check for URLs - require letters in domain and TLD of 2+ chars
            const urlRegex = /^(https?:\/\/)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(\/[\w-./?%&=]*)?$/
            if (urlRegex.test(word)) {
                const url = word.startsWith('http') ? word : `https://${word}`
                return (
                    <Fragment key={index}>
                        <a
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary hover:underline break-all"
                            onClick={e => e.stopPropagation()}
                        >
                            {word}
                        </a>
                        {index !== words.length - 1 ? ' ' : ''}
                    </Fragment>
                )
            }

            // Check @mentions first
            if (word.startsWith('@') && !disableMentions) {
                // Match username that can include periods only in domain-like structure
                const mentionMatch = word.match(
                    /^@[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?)*(?:@[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?)*)?/
                )
                if (mentionMatch) {
                    const mention = mentionMatch[0]
                    const punctuation = word.slice(mention.length).match(/^[.,!?]+/)?.[0] ?? ''
                    const remainingText = word.slice(mention.length + punctuation.length)

                    const handle = mention.substring(1)

                    return (
                        <Fragment key={index}>
                            <a
                                href={`/profile/${encodeURIComponent(handle)}`}
                                className="text-primary hover:underline"
                                onClick={e => handleMentionClick(e, handle)}
                            >
                                {mention}
                            </a>
                            {punctuation}
                            {remainingText}
                            {index !== words.length - 1 ? ' ' : ''}
                        </Fragment>
                    )
                }
            }

            // Check hashtags second
            if (word.startsWith('#')) {
                // Match only the hashtag without punctuation
                const hashtagMatch = word.match(/^#\w+/)
                if (hashtagMatch) {
                    const hashtag = hashtagMatch[0]
                    const punctuation = word.slice(hashtag.length).match(/^[.,!?]+/)?.[0] ?? ''
                    const remainingText = word.slice(hashtag.length + punctuation.length)

                    // Strip the leading # for the route parameter
                    const tag = hashtag.substring(1)

                    return (
                        <Fragment key={index}>
                            <a
                                href={`/hashtag/${encodeURIComponent(tag)}`}
                                className="text-primary hover:underline"
                                onClick={e => handleHashtagClick(e, tag)}
                            >
                                {hashtag}
                            </a>
                            {punctuation}
                            {remainingText}
                            {index !== words.length - 1 ? ' ' : ''}
                        </Fragment>
                    )
                }
            }

            // Regular word handling
            return (
                <Fragment key={index}>
                    {word}
                    {index !== words.length - 1 ? ' ' : ''}
                </Fragment>
            )
        })
    }

    return (
        <p className={`whitespace-pre-wrap break-words ${className}`}>
            {text.split('\n').map((line, i) => (
                <Fragment key={i}>
                    {renderLine(line)}
                    {i !== text.split('\n').length - 1 && <br />}
                </Fragment>
            ))}
            {trailing ? <span className="block">{trailing}</span> : null}
        </p>
    )
}

/**
 * Renders text using AT Protocol facets for precise link/mention/hashtag placement.
 */
function FacetRenderer({
    text,
    facets,
    disableMentions,
    className,
    trailing,
    onMentionClick,
    onHashtagClick,
}: {
    text: string
    facets: PostFacet[]
    disableMentions: boolean
    className: string
    trailing?: ReactNode
    onMentionClick: (e: React.MouseEvent, handle: string) => void
    onHashtagClick: (e: React.MouseEvent, tag: string) => void
}) {
    const segments = useMemo(() => segmentByFacets(text, facets), [text, facets])

    return (
        <p className={`whitespace-pre-wrap break-words ${className}`}>
            {segments.map((segment, i) => {
                if (!segment.facet) {
                    return <Fragment key={i}>{segment.text}</Fragment>
                }

                // Find the primary feature of this facet
                const feature = segment.facet.features[0]
                if (!feature) {
                    return <Fragment key={i}>{segment.text}</Fragment>
                }

                if (feature.type === 'link' && feature.uri) {
                    if (!/^https?:\/\//i.test(feature.uri)) {
                        return <Fragment key={i}>{segment.text}</Fragment>
                    }
                    return (
                        <a
                            key={i}
                            href={feature.uri}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary hover:underline break-all"
                            onClick={e => e.stopPropagation()}
                        >
                            {segment.text}
                        </a>
                    )
                }

                if (feature.type === 'mention' && !disableMentions) {
                    // Use the text content (e.g., "@handle.bsky.social") for display
                    const handle = segment.text.startsWith('@')
                        ? segment.text.substring(1)
                        : segment.text
                    return (
                        <a
                            key={i}
                            href={`/profile/${encodeURIComponent(handle)}`}
                            className="text-primary hover:underline"
                            onClick={e => onMentionClick(e, handle)}
                        >
                            {segment.text}
                        </a>
                    )
                }

                if (feature.type === 'tag' && feature.tag) {
                    return (
                        <a
                            key={i}
                            href={`/hashtag/${encodeURIComponent(feature.tag)}`}
                            className="text-primary hover:underline"
                            onClick={e => onHashtagClick(e, feature.tag!)}
                        >
                            {segment.text}
                        </a>
                    )
                }

                return <Fragment key={i}>{segment.text}</Fragment>
            })}
            {trailing ? <span className="block">{trailing}</span> : null}
        </p>
    )
}
